import { useId, useMemo, useState, type ReactNode } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DownloadIcon, PrinterIcon } from 'lucide-react';
import { useParams } from 'react-router';

import useDebounced from '@mds/shared/hooks/use-debounced';
import { ApiError } from '@mds/shared/lib/api';
import { dateKeyOf, dateLabel } from '@mds/shared/lib/format';
import {
  checkSurveyAnswers,
  conditionHolds,
  isChoiceQuestion,
  surveyMaxLength,
  surveyQuestions,
  tickSurveyOptions,
} from '@mds/shared/lib/survey';
import { cn } from '@mds/shared/lib/utils';
import {
  SURVEY_CHANNELS,
  type AdminSurvey,
  type AdminSurveyResponse,
  type SurveyAnswer,
  type SurveyChannel,
  type SurveyDefinition,
  type SurveyItem,
  type SurveyLanguage,
  type SurveyQuestion,
} from '@mds/shared/types/survey';
import { ChoiceList } from '@mds/shared/ui/form/choice-list';
import Select from '@mds/shared/ui/select';

import { useSession } from '@/auth/use-session';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { FieldError, controlCls, inputCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import PrintFrame from '@/components/reporting/print-frame';
import StatCard from '@/components/ui/stat-card';
import { Cell, Row, Table } from '@/components/ui/table';
import Tabs from '@/components/ui/tabs';
import { SegmentedToggle, Toolbar } from '@/components/ui/toolbar';
import {
  CHANNEL_LABELS,
  LANGUAGE_LABELS,
  answerText,
  answerTo,
  count,
  followUpKeys,
  followUpsOf,
  readQuestions,
  responseSchema,
  serverErrors,
  textIn,
  type ResponseDraft,
} from '@/domain/surveys';
import { useUnsaved } from '@/hooks/use-unsaved';
import { printDialog, stamp } from '@/lib/export';
import { useValidation } from '@/lib/validation';
import {
  answerRows,
  definitionsFor,
  exportAllXlsx,
  exportResponseXlsx,
  fetchAllResponses,
} from '@/surveys/survey-export';
import * as surveys from '@/surveys/surveys.api';

type Tab = 'responses' | 'form';

const ALL = 'all';
const PAGE_SIZE = 20;

/** The page's own words. The questions themselves come with the survey's definition. */
const T = {
  en: {
    tabs: { responses: 'Responses', new: 'Enter a response', edit: 'Edit response' },
    intro:
      'Type in a questionnaire as it was filled in. Tick what they ticked; skip a question they were told to skip.',
    details: 'Response details',
    collectedOn: 'Date filled in',
    channel: 'How it was collected',
    pickOne: 'Pick one',
    pickMany: 'Tick all that apply',
    pickUpTo: (n: string) => `Tick up to ${n}`,
    optional: 'Optional',
    clear: 'Clear form',
    cancel: 'Cancel',
    save: 'Save response',
    saveEdit: 'Save changes',
    saving: 'Saving…',
    saved: 'Response saved. It is at the top of Responses.',
    updated: 'Changes saved.',
    deleted: 'Response deleted.',
    refused: 'Some answers were refused. They are marked above.',
    failed: 'That did not go through. Try again.',
    unreachable: 'The server could not be reached.',
    total: ['Responses', 'entered so far'] as const,
    showOnly: (label: string) => `Show only: ${label}`,
    responses: (n: string) => `${n} responses`,
    respondent: 'Response',
    filledIn: 'Filled in',
    anonymous: 'Anonymous',
    none: '—',
    repeat: 'Repeat',
    repeats: (n: number, what: string) =>
      `Same ${what.toLocaleLowerCase('en')} as ${n} other ${n === 1 ? 'response' : 'responses'}.`,
    search: 'Names, phones, answers',
    searchLabel: 'Search responses',
    every: 'Every response',
    filterLabel: 'Filter responses',
    loading: 'Loading responses…',
    loadingOne: 'Loading the response…',
    loadFailed: 'Could not load the responses',
    readFailed: 'That response could not be read.',
    empty: 'No responses yet',
    emptyHint: 'Enter the first questionnaire from the other tab.',
    emptyHintReader: 'Responses appear here once staff type them in.',
    noMatch: 'No response matches',
    noMatchHint: 'Try a different search or filter.',
    close: 'Close',
    edit: 'Edit',
    remove: 'Delete',
    removeTitle: 'Delete this response?',
    removeBody:
      'It leaves the list and the counts. The audit log keeps a record that it was deleted.',
    removeConfirm: 'Delete response',
    removing: 'Deleting…',
    back: 'Back',
    enteredIn: 'Entered in',
    by: 'by',
    editedBy: 'Last edited by',
    pdf: 'PDF',
    excel: 'Excel',
    preparing: 'Preparing…',
    exportAllPdf: 'Export every response as a PDF',
    exportAllExcel: 'Export every response as an Excel workbook',
    exportFailed: 'The export could not be generated. Try again.',
    allTitle: (n: string) => `All responses (${n})`,
    savePdf: 'Save as PDF',
    printed: 'Printed',
  },
  ar: {
    tabs: { responses: 'الإجابات', new: 'إدخال إجابة', edit: 'تعديل الإجابة' },
    intro:
      'أدخلوا الاستبيان كما عُبّئ. ضعوا علامة حيث وضعوا، وتجاوزوا السؤال الذي طُلب منهم تجاوزه.',
    details: 'تفاصيل الإجابة',
    collectedOn: 'تاريخ التعبئة',
    channel: 'طريقة الجمع',
    pickOne: 'اختاروا إجابة واحدة',
    pickMany: 'يمكن اختيار أكثر من إجابة',
    pickUpTo: (n: string) => `اختاروا ${n} كحد أقصى`,
    optional: 'اختياري',
    clear: 'مسح النموذج',
    cancel: 'إلغاء',
    save: 'حفظ الإجابة',
    saveEdit: 'حفظ التعديلات',
    saving: 'جارٍ الحفظ…',
    saved: 'حُفظت الإجابة. تجدونها في أعلى قائمة الإجابات.',
    updated: 'حُفظت التعديلات.',
    deleted: 'حُذفت الإجابة.',
    refused: 'رُفضت بعض الإجابات، وهي مُعلَّمة أعلاه.',
    failed: 'لم تتم العملية. حاولوا مجدداً.',
    unreachable: 'تعذّر الوصول إلى الخادم.',
    total: ['الإجابات', 'حتى الآن'] as const,
    showOnly: (label: string) => `عرض فقط: ${label}`,
    responses: (n: string) => `الإجابات: ${n}`,
    respondent: 'الإجابة',
    filledIn: 'تاريخ التعبئة',
    anonymous: 'دون اسم',
    none: '—',
    repeat: 'مكرّر',
    repeats: (n: number, what: string) =>
      `${what} نفسه موجود في ${n.toLocaleString('ar-LB')} من الإجابات الأخرى.`,
    search: 'الأسماء، الهواتف، الإجابات',
    searchLabel: 'البحث في الإجابات',
    every: 'كل الإجابات',
    filterLabel: 'تصفية الإجابات',
    loading: 'جارٍ تحميل الإجابات…',
    loadingOne: 'جارٍ تحميل الإجابة…',
    loadFailed: 'تعذّر تحميل الإجابات',
    readFailed: 'تعذّرت قراءة هذه الإجابة.',
    empty: 'لا إجابات بعد',
    emptyHint: 'أدخلوا أول استبيان من التبويب الآخر.',
    emptyHintReader: 'تظهر الإجابات هنا بعد أن يدخلها الفريق.',
    noMatch: 'لا إجابة مطابقة',
    noMatchHint: 'جرّبوا بحثاً أو تصفية أخرى.',
    close: 'إغلاق',
    edit: 'تعديل',
    remove: 'حذف',
    removeTitle: 'حذف هذه الإجابة؟',
    removeBody: 'ستختفي من القائمة ومن الأرقام، ويحتفظ سجل التدقيق بأنها حُذفت.',
    removeConfirm: 'حذف الإجابة',
    removing: 'جارٍ الحذف…',
    back: 'رجوع',
    enteredIn: 'أُدخلت بـ',
    by: 'بواسطة',
    editedBy: 'آخر تعديل بواسطة',
    pdf: 'PDF',
    excel: 'Excel',
    preparing: 'جارٍ التحضير…',
    exportAllPdf: 'تصدير كل الإجابات كملف PDF',
    exportAllExcel: 'تصدير كل الإجابات كملف Excel',
    exportFailed: 'تعذّر إنشاء الملف. حاولوا مجدداً.',
    allTitle: (n: string) => `كل الإجابات (${n})`,
    savePdf: 'حفظ كملف PDF',
    printed: 'طُبع في',
  },
} as const;

const failure = (error: unknown, fallback: string) =>
  error instanceof ApiError ? error.message : fallback;

/** A question by key, from wherever it sits in the definition. */
const questionsByKey = (definition: SurveyDefinition) =>
  new Map(readQuestions(definition).map(({ question }) => [question.key, question]));

/** The heading a question goes by in a column: its short label, or failing that its label. */
const heading = (question: SurveyQuestion, language: SurveyLanguage) =>
  textIn(question.shortLabel, language) || textIn(question.label, language);

/** What names a response: its answer to the listing's title question. */
function responseTitle(
  definition: SurveyDefinition,
  response: AdminSurveyResponse,
  language: SurveyLanguage,
) {
  const question = definition.listing.title
    ? questionsByKey(definition).get(definition.listing.title)
    : undefined;
  return (
    (question && answerText(question, response.answers, followUpsOf(definition), language)) ||
    T[language].anonymous
  );
}

/**
 * One survey: staff type in the questionnaires respondents filled in, in any of the survey's
 * languages, and read back what came in. Everything about the questions — their wording, options,
 * branching and the list's columns — comes from the survey's definition.
 */
export default function SurveyPage() {
  const { surveyKey = '' } = useParams();
  const survey = useQuery({
    queryKey: ['survey', surveyKey],
    queryFn: () => surveys.getSurvey(surveyKey),
    enabled: surveyKey !== '',
  });

  if (survey.isPending) {
    return <p className="m-0 text-base text-ink-muted">Loading the survey…</p>;
  }
  if (survey.isError) {
    const missing = survey.error instanceof ApiError && survey.error.status === 404;
    return (
      <Card>
        <EmptyState
          title={missing ? 'No such survey' : 'Could not load the survey'}
          hint={failure(survey.error, T.en.unreachable)}
        />
      </Card>
    );
  }
  return <SurveyScreen key={survey.data.key} survey={survey.data} />;
}

function SurveyScreen({ survey }: { survey: AdminSurvey }) {
  const { definition } = survey;
  const key = survey.key;
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('surveys.manage');

  const [language, setLanguage] = useState<SurveyLanguage>(definition.languages[0] ?? 'en');
  const [tab, setTab] = useState<Tab>('responses');
  const [editing, setEditing] = useState<AdminSurveyResponse | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const t = T[language];
  const dir = language === 'ar' ? 'rtl' : 'ltr';

  const detail = useQuery({
    queryKey: ['survey-response', key, openId],
    queryFn: () => surveys.getSurveyResponse(key, openId as string),
    enabled: openId !== null,
  });
  const open = openId !== null ? (detail.data ?? null) : null;

  // A response saved against an older version reads in that version's words.
  const older = open !== null && open.surveyVersion !== survey.version;
  const version = useQuery({
    queryKey: ['survey-version', key, open?.surveyVersion],
    queryFn: () => surveys.getSurveyVersion(key, open!.surveyVersion),
    enabled: older,
    staleTime: Infinity,
  });
  const readDefinition = older ? (version.data?.definition ?? definition) : definition;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['survey-responses', key] }),
      queryClient.invalidateQueries({ queryKey: ['survey-summary', key] }),
    ]);

  const save = async (draft: ResponseDraft) => {
    const answers = checkSurveyAnswers(definition, draft.answers).answers;
    const channel = draft.channel as SurveyChannel;
    const saved = editing
      ? await surveys.updateSurveyResponse(key, editing.id, {
          collectedOn: draft.collectedOn,
          channel,
          answers,
        })
      : await surveys.createSurveyResponse(key, {
          collectedOn: draft.collectedOn,
          channel,
          language,
          answers,
        });
    queryClient.setQueryData(['survey-response', key, saved.id], saved);
    await refresh();
    setNotice(editing ? t.updated : t.saved);
    setEditing(null);
    setTab('responses');
  };

  const remove = async () => {
    if (!open) return;
    setBusy(true);
    setProblem(null);
    try {
      await surveys.deleteSurveyResponse(key, open.id);
      queryClient.removeQueries({ queryKey: ['survey-response', key, open.id] });
      await refresh();
      setRemoving(false);
      setOpenId(null);
      setNotice(t.deleted);
    } catch (e) {
      setProblem(failure(e, t.failed));
    } finally {
      setBusy(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const openTitle = open ? responseTitle(readDefinition, open, language) : t.loadingOne;

  const exportXlsx = async () => {
    if (!open) return;
    setExporting(true);
    setProblem(null);
    try {
      await exportResponseXlsx(survey, open, readDefinition, language, openTitle);
    } catch {
      setProblem(t.exportFailed);
    } finally {
      setExporting(false);
    }
  };

  const edit = (response: AdminSurveyResponse) => {
    setOpenId(null);
    setNotice(null);
    setEditing(response);
    setTab('form');
  };

  return (
    <div className="flex flex-col gap-6" dir={dir} lang={language}>
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hairline bg-surface px-5 py-4 shadow-card">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-lg font-semibold">{textIn(definition.title, language)}</span>
          {definition.intro && (
            <span className="text-base text-ink-muted">{textIn(definition.intro, language)}</span>
          )}
        </div>
        {definition.languages.length > 1 && (
          <SegmentedToggle
            aria-label="Language"
            value={language}
            options={definition.languages.map((l) => ({
              value: l,
              label: LANGUAGE_LABELS[l],
              lang: l,
            }))}
            onChange={setLanguage}
          />
        )}
      </div>

      {mayManage && (
        <Tabs
          label={textIn(definition.title, language)}
          tabs={[
            { key: 'responses', label: t.tabs.responses },
            { key: 'form', label: editing ? t.tabs.edit : t.tabs.new },
          ]}
          active={tab}
          onPick={(next) => {
            setNotice(null);
            if (next === 'responses') setEditing(null);
            setTab(next);
          }}
        />
      )}

      {notice && tab === 'responses' && (
        <p
          role="status"
          className="m-0 rounded-xl border border-brand-edge/35 bg-brand-wash px-4 py-3 text-base text-brand-strong"
        >
          {notice}
        </p>
      )}

      {tab === 'responses' || !mayManage ? (
        <Responses
          survey={survey}
          language={language}
          mayManage={mayManage}
          onOpen={(id) => {
            setProblem(null);
            setOpenId(id);
          }}
        />
      ) : (
        <SurveyForm
          key={editing?.id ?? 'new'}
          definition={definition}
          language={language}
          initial={editing ? draftOf(editing, definition) : blankDraft()}
          editing={editing !== null}
          onSave={save}
          onCancel={() => {
            setEditing(null);
            setTab('responses');
          }}
        />
      )}

      <Modal
        open={openId !== null && !removing}
        onClose={() => setOpenId(null)}
        title={openTitle}
        footer={
          <>
            {open && mayManage && (
              <>
                <Button onClick={() => edit(open)}>{t.edit}</Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setProblem(null);
                    setRemoving(true);
                  }}
                >
                  {t.remove}
                </Button>
              </>
            )}
            {open && (
              <>
                <Button
                  variant="quiet"
                  onClick={() => printDialog(`${textIn(definition.title, language)} ${openTitle}`)}
                >
                  <PrinterIcon className="size-5" aria-hidden="true" />
                  {t.pdf}
                </Button>
                <Button variant="quiet" disabled={exporting} onClick={() => void exportXlsx()}>
                  <DownloadIcon className="size-5" aria-hidden="true" />
                  {exporting ? t.preparing : t.excel}
                </Button>
              </>
            )}
            <Button variant="quiet" onClick={() => setOpenId(null)}>
              {t.close}
            </Button>
          </>
        }
      >
        {detail.isPending ? (
          <p className="m-0 text-base text-ink-muted">{t.loadingOne}</p>
        ) : detail.isError || !open ? (
          <Banner>{failure(detail.error, t.readFailed)}</Banner>
        ) : (
          <>
            {problem && <Banner>{problem}</Banner>}
            <PrintFrame
              title={openTitle}
              subtitle={textIn(definition.title, language)}
              lines={[`${t.printed} ${stamp()}`]}
            >
              <ResponseDetail response={open} definition={readDefinition} language={language} />
            </PrintFrame>
          </>
        )}
      </Modal>

      <Modal
        open={removing && open !== null}
        onClose={() => !busy && setRemoving(false)}
        title={t.removeTitle}
        footer={
          <>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              {busy ? t.removing : t.removeConfirm}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setRemoving(false)}>
              {t.back}
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        <p className="m-0 text-base">{t.removeBody}</p>
      </Modal>
    </div>
  );
}

const blankDraft = (): ResponseDraft => ({
  collectedOn: dateKeyOf(new Date()),
  channel: 'paper',
  answers: {},
});

/** A saved response to edit. Answers to questions the survey has since dropped are left out. */
function draftOf(response: AdminSurveyResponse, definition: SurveyDefinition): ResponseDraft {
  const known = new Set(surveyQuestions(definition).map(({ question }) => question.key));
  return {
    collectedOn: response.collectedOn,
    channel: response.channel,
    answers: Object.fromEntries(Object.entries(response.answers).filter(([k]) => known.has(k))),
  };
}

/* ---------------------------------------------------------------- responses */

function Responses({
  survey,
  language,
  mayManage,
  onOpen,
}: {
  survey: AdminSurvey;
  language: SurveyLanguage;
  mayManage: boolean;
  onOpen: (id: string) => void;
}) {
  const t = T[language];
  const { definition, key } = survey;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const settled = useDebounced(search);
  const [highlight, setHighlight] = useState<string>(ALL);

  const list = useQuery({
    queryKey: ['survey-responses', key, { page, settled, highlight }],
    queryFn: () =>
      surveys.listSurveyResponses(key, {
        page,
        limit: PAGE_SIZE,
        search: settled || undefined,
        highlight: highlight === ALL ? undefined : highlight,
      }),
  });
  const summary = useQuery({
    queryKey: ['survey-summary', key],
    queryFn: () => surveys.getSurveySummary(key),
  });

  const { titleQuestion, columns, followUps } = useMemo(() => {
    const byKey = questionsByKey(definition);
    return {
      titleQuestion: definition.listing.title ? byKey.get(definition.listing.title) : undefined,
      columns: definition.listing.columns
        .map((k) => byKey.get(k))
        .filter((q): q is SurveyQuestion => q !== undefined),
      followUps: followUpsOf(definition),
    };
  }, [definition]);
  const tagsLabel = definition.listing.tagsLabel;
  const tagged = definition.highlights.filter((h) => h.tag);

  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [printing, setPrinting] = useState<{
    responses: AdminSurveyResponse[];
    definitions: Map<number, SurveyDefinition>;
  } | null>(null);

  const exportAll = async (format: 'pdf' | 'xlsx') => {
    setExporting(format);
    setExportError(null);
    try {
      const responses = await fetchAllResponses(key);
      const definitions = await definitionsFor(survey, responses);
      if (format === 'xlsx') await exportAllXlsx(survey, responses, definitions, language);
      else setPrinting({ responses, definitions });
    } catch (e) {
      setExportError(failure(e, t.exportFailed));
    } finally {
      setExporting(null);
    }
  };

  const rows = list.data?.data ?? [];
  const filtered = Boolean(settled) || highlight !== ALL;
  const pick = (next: string) => {
    setHighlight(next);
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          density="compact"
          label={t.total[0]}
          value={summary.data ? count(summary.data.total, language) : '–'}
          note={t.total[1]}
        />
        {definition.highlights.map((h) => (
          <StatCard
            key={h.key}
            density="compact"
            label={textIn(h.label, language)}
            value={summary.data ? count(summary.data.highlights[h.key] ?? 0, language) : '–'}
            note={textIn(h.note, language)}
            onClick={() => pick(highlight === h.key ? ALL : h.key)}
            actionLabel={t.showOnly(textIn(h.label, language))}
          />
        ))}
      </div>

      <Card
        title={
          list.data ? t.responses(count(list.data.pagination.total, language)) : t.tabs.responses
        }
        action={
          <Toolbar>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t.search}
              aria-label={t.searchLabel}
              maxLength={160}
              className={cn(controlCls, 'w-64')}
            />
            {definition.highlights.length > 0 && (
              <Select
                value={highlight}
                onChange={pick}
                options={[
                  { value: ALL, label: t.every },
                  ...definition.highlights.map((h) => ({
                    value: h.key,
                    label: textIn(h.label, language),
                  })),
                ]}
                aria-label={t.filterLabel}
              />
            )}
            <Button
              variant="quiet"
              disabled={exporting !== null}
              aria-label={t.exportAllPdf}
              onClick={() => void exportAll('pdf')}
            >
              <PrinterIcon className="size-5" aria-hidden="true" />
              {exporting === 'pdf' ? t.preparing : t.pdf}
            </Button>
            <Button
              variant="quiet"
              disabled={exporting !== null}
              aria-label={t.exportAllExcel}
              onClick={() => void exportAll('xlsx')}
            >
              <DownloadIcon className="size-5" aria-hidden="true" />
              {exporting === 'xlsx' ? t.preparing : t.excel}
            </Button>
          </Toolbar>
        }
      >
        {exportError && <Banner>{exportError}</Banner>}
        {list.isPending ? (
          <p className="m-0 text-base text-ink-muted">{t.loading}</p>
        ) : list.isError ? (
          <EmptyState title={t.loadFailed} hint={failure(list.error, t.unreachable)} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={filtered ? t.noMatch : t.empty}
            hint={filtered ? t.noMatchHint : mayManage ? t.emptyHint : t.emptyHintReader}
          />
        ) : (
          <div dir="ltr">
            {/* The table lays out left to right; each cell sets its own direction. */}
            <Table
              head={[
                titleQuestion ? heading(titleQuestion, language) : t.respondent,
                ...columns.map((q) => heading(q, language)),
                ...(tagsLabel ? [textIn(tagsLabel, language)] : []),
                t.filledIn,
              ]}
            >
              {rows.map((r) => {
                const tags = tagged.filter((h) => conditionHolds(h.when, r.answers));
                return (
                  <Row key={r.id} onClick={() => onOpen(r.id)}>
                    <Cell className="font-semibold">
                      <span className="flex flex-wrap items-center gap-2">
                        <span dir="auto">
                          {(titleQuestion &&
                            answerText(titleQuestion, r.answers, followUps, language)) ||
                            t.anonymous}
                        </span>
                        {r.repeats > 0 && (
                          <Chip
                            size="sm"
                            className="border border-warn-edge bg-warn-wash text-warn-text"
                          >
                            <span lang={language}>{t.repeat}</span>
                          </Chip>
                        )}
                      </span>
                    </Cell>
                    {columns.map((q) => (
                      <Cell key={q.key}>
                        <span dir="auto">
                          {answerText(q, r.answers, followUps, language, { followUpOnly: true }) ||
                            t.none}
                        </span>
                      </Cell>
                    ))}
                    {tagsLabel && (
                      <Cell>
                        <span className="flex flex-wrap gap-1.5">
                          {tags.length > 0
                            ? tags.map((h) => (
                                <Chip key={h.key} size="sm">
                                  {textIn(h.tag, language)}
                                </Chip>
                              ))
                            : t.none}
                        </span>
                      </Cell>
                    )}
                    <Cell className="tabular-nums">{dateLabel(r.collectedOn)}</Cell>
                  </Row>
                );
              })}
            </Table>
          </div>
        )}
        <Pager pagination={list.data?.pagination} onPage={setPage} />
      </Card>

      <Modal
        open={printing !== null}
        onClose={() => setPrinting(null)}
        title={t.allTitle(count(printing?.responses.length ?? 0, language))}
        footer={
          <>
            <Button
              onClick={() =>
                printDialog(`${textIn(definition.title, language)} ${t.tabs.responses}`)
              }
            >
              <PrinterIcon className="size-5" aria-hidden="true" />
              {t.savePdf}
            </Button>
            <Button variant="quiet" onClick={() => setPrinting(null)}>
              {t.close}
            </Button>
          </>
        }
      >
        {printing && (
          <PrintFrame
            title={t.allTitle(count(printing.responses.length, language))}
            subtitle={textIn(definition.title, language)}
            lines={[`${t.printed} ${stamp()}`]}
          >
            {printing.responses.length === 0 ? (
              <p className="m-0 text-base text-ink-muted">{t.empty}</p>
            ) : (
              <div className="flex flex-col gap-8">
                {printing.responses.map((r) => {
                  const read = printing.definitions.get(r.surveyVersion) ?? definition;
                  return (
                    <section key={r.id} className="flex flex-col gap-3">
                      <h3 className="m-0 text-lg font-semibold" dir="auto">
                        {responseTitle(read, r, language)}
                      </h3>
                      <ResponseDetail response={r} definition={read} language={language} />
                    </section>
                  );
                })}
              </div>
            )}
          </PrintFrame>
        )}
      </Modal>
    </div>
  );
}

/** Every answer of one response, question by question, in the language being read. */
function ResponseDetail({
  response: r,
  definition,
  language,
}: {
  response: AdminSurveyResponse;
  definition: SurveyDefinition;
  language: SurveyLanguage;
}) {
  const t = T[language];
  const identity = definition.identity
    ? questionsByKey(definition).get(definition.identity)
    : undefined;

  const rows = [
    {
      key: 'collected',
      question: t.collectedOn,
      answer: `${dateLabel(r.collectedOn)} · ${CHANNEL_LABELS[r.channel][language]}`,
    },
    ...answerRows(r, definition, language),
  ];

  return (
    <div dir={language === 'ar' ? 'rtl' : 'ltr'} lang={language} className="flex flex-col gap-4">
      {r.repeats > 0 && identity && (
        <p className="m-0 rounded-xl border border-warn-edge bg-warn-wash px-4 py-3 text-base text-warn-text">
          {t.repeats(r.repeats, heading(identity, language))}
        </p>
      )}
      <dl className="m-0 flex flex-col gap-3">
        {rows.map((row) => (
          <div key={row.key} className="flex flex-col gap-1 border-b border-hairline/70 pb-3">
            <dt className="text-base text-ink-muted">{row.question}</dt>
            <dd className="m-0 text-base font-semibold" dir="auto">
              {row.answer}
            </dd>
          </div>
        ))}
      </dl>
      <p className="m-0 text-sm text-ink-muted">
        {t.enteredIn} {LANGUAGE_LABELS[r.language]} {t.by} {r.enteredBy.name}
        {r.updatedBy && (
          <>
            {' · '}
            {t.editedBy} {r.updatedBy.name}
          </>
        )}
      </p>
    </div>
  );
}

/* --------------------------------------------------------------------- form */

function SurveyForm({
  definition,
  language,
  initial,
  editing,
  onSave,
  onCancel,
}: {
  definition: SurveyDefinition;
  language: SurveyLanguage;
  initial: ResponseDraft;
  editing: boolean;
  onSave: (draft: ResponseDraft) => Promise<void>;
  onCancel: () => void;
}) {
  const t = T[language];
  const [draft, setDraft] = useState<ResponseDraft>(initial);
  const [refused, setRefused] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const schema = useMemo(() => responseSchema(definition, language), [definition, language]);
  const validation = useValidation(schema, draft);
  useUnsaved(!busy && JSON.stringify(draft) !== JSON.stringify(initial));

  // Branches open on the answers that would be kept, so a closed branch cannot open another.
  const kept = useMemo(
    () => checkSurveyAnswers(definition, draft.answers).answers,
    [definition, draft.answers],
  );
  const conditions = useMemo(
    () => new Map(surveyQuestions(definition).map((a) => [a.question.key, a.conditions])),
    [definition],
  );
  const followUps = useMemo(() => followUpsOf(definition), [definition]);
  const inline = useMemo(() => followUpKeys(followUps), [followUps]);
  const asked = (q: SurveyQuestion) =>
    (conditions.get(q.key) ?? []).every((c) => conditionHolds(c, kept));

  /** A field's error: the server's refusal until it is edited, then the form's own check. */
  const field = (path: string) => {
    const bound = validation.field(path);
    return { ...bound, error: refused[path] ?? bound.error };
  };

  const forget = (path: string) =>
    setRefused((prev) => {
      if (!(path in prev)) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });

  const setAnswer = (key: string, value: SurveyAnswer) => {
    setDraft((d) => ({ ...d, answers: { ...d.answers, [key]: value } }));
    forget(`answers.${key}`);
  };

  const setDetail = <K extends 'collectedOn' | 'channel'>(key: K, value: ResponseDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    forget(key);
  };

  const options = (q: SurveyQuestion) =>
    (q.options ?? []).map((o) => ({ value: o.value, label: textIn(o.label, language) }));

  const choiceHint = (q: SurveyQuestion) =>
    [
      q.type === 'single'
        ? t.pickOne
        : q.maxSelections !== undefined
          ? t.pickUpTo(count(q.maxSelections, language))
          : t.pickMany,
      textIn(q.hint, language),
      !q.required && t.optional,
    ]
      .filter(Boolean)
      .join('. ');

  const typed = (q: SurveyQuestion, number?: string) => {
    if (!asked(q)) return null;
    const answer = answerTo(draft.answers, q.key);
    const common = {
      value: typeof answer === 'string' ? answer : '',
      onChange: (e: { target: { value: string } }) => setAnswer(q.key, e.target.value),
    };
    const hint = [!q.required && t.optional, textIn(q.hint, language)].filter(Boolean).join('. ');
    return (
      <Field
        key={q.key}
        label={`${number ? `${number}. ` : ''}${textIn(q.label, language)}`}
        hint={hint || undefined}
        required={q.required}
        {...field(`answers.${q.key}`)}
      >
        {q.type === 'textarea' ? (
          <textarea rows={3} className={inputCls} maxLength={surveyMaxLength(q)} {...common} />
        ) : q.type === 'phone' ? (
          <input
            type="tel"
            dir="ltr"
            className={cn(inputCls, 'text-start')}
            maxLength={surveyMaxLength(q)}
            placeholder="+961 …"
            {...common}
          />
        ) : q.type === 'date' ? (
          <input type="date" className={inputCls} {...common} />
        ) : (
          <input className={inputCls} maxLength={surveyMaxLength(q)} {...common} />
        )}
      </Field>
    );
  };

  const question = (q: SurveyQuestion) => {
    if (!asked(q)) return null;
    if (!isChoiceQuestion(q)) return inline.has(q.key) ? null : typed(q, q.number);
    const path = `answers.${q.key}`;
    const { error, onBlur } = field(path);
    const answer = answerTo(draft.answers, q.key);
    const explainers = [
      ...new Set(
        [...followUps].filter(([at]) => at.startsWith(`${q.key}:`)).map(([, follow]) => follow),
      ),
    ];
    return (
      <Question
        key={q.key}
        n={q.number}
        label={textIn(q.label, language)}
        hint={choiceHint(q)}
        error={error}
      >
        {q.type === 'multiple' ? (
          <ChoiceList
            multiple
            options={options(q)}
            value={Array.isArray(answer) ? answer : []}
            onBlur={onBlur}
            onChange={(next) =>
              setAnswer(
                q.key,
                tickSurveyOptions(q, Array.isArray(answer) ? answer : [], next as string[]),
              )
            }
            {...aria(error)}
          />
        ) : (
          <ChoiceList
            options={options(q)}
            value={typeof answer === 'string' ? answer : ''}
            onBlur={onBlur}
            onChange={(next) => setAnswer(q.key, next as string)}
            {...aria(error)}
          />
        )}
        {explainers.map((follow) => typed(follow))}
      </Question>
    );
  };

  const item = (it: SurveyItem) => {
    if (it.kind === 'question') return question(it);
    if (it.showIf && !conditionHolds(it.showIf, kept)) return null;
    const drawn = it.questions.map(question).filter(Boolean);
    if (drawn.length === 0) return null;
    if (it.title) {
      return (
        <Branch
          key={it.key}
          n={it.number}
          title={textIn(it.title, language)}
          description={textIn(it.description, language)}
        >
          {drawn}
        </Branch>
      );
    }
    return (
      <div
        key={it.key}
        className="flex flex-col gap-5 rounded-xl border border-hairline bg-raised p-4 sm:p-5"
      >
        {it.description && (
          <p className="m-0 text-base text-ink-muted">{textIn(it.description, language)}</p>
        )}
        {drawn}
      </div>
    );
  };

  const submit = async () => {
    if (!validation.check()) return;
    setBusy(true);
    setProblem(null);
    setRefused({});
    try {
      await onSave(draft);
    } catch (e) {
      const errors = serverErrors(e, definition, draft.answers, language);
      if (Object.keys(errors).length > 0) {
        setRefused(errors);
        setProblem(t.refused);
      } else {
        setProblem(failure(e, t.failed));
      }
      setBusy(false);
    }
  };

  const channel = field('channel');

  return (
    <div className="flex flex-col gap-6">
      <p className="m-0 text-base text-ink-muted">{t.intro}</p>

      <Card title={t.details}>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label={t.collectedOn} required {...field('collectedOn')}>
            <input
              type="date"
              className={inputCls}
              value={draft.collectedOn}
              onChange={(e) => setDetail('collectedOn', e.target.value)}
            />
          </Field>
          <Question label={t.channel} hint={t.pickOne} error={channel.error}>
            <ChoiceList
              options={SURVEY_CHANNELS.map((c) => ({
                value: c,
                label: CHANNEL_LABELS[c][language],
              }))}
              value={draft.channel}
              onBlur={channel.onBlur}
              onChange={(next) => setDetail('channel', next as SurveyChannel)}
              {...aria(channel.error)}
            />
          </Question>
        </div>
      </Card>

      {definition.sections.map((section) => (
        <Card key={section.key} title={textIn(section.title, language)}>
          <div className="flex flex-col gap-8">
            {section.description && (
              <p className="m-0 text-base text-ink-muted">
                {textIn(section.description, language)}
              </p>
            )}
            {section.items.map(item)}
          </div>
        </Card>
      ))}

      {problem && <Banner>{problem}</Banner>}

      <div className="flex flex-wrap justify-end gap-2">
        {editing ? (
          <Button variant="quiet" disabled={busy} onClick={onCancel}>
            {t.cancel}
          </Button>
        ) : (
          <Button
            variant="quiet"
            disabled={busy}
            onClick={() => {
              setDraft(blankDraft());
              setRefused({});
              setProblem(null);
              validation.reset();
            }}
          >
            {t.clear}
          </Button>
        )}
        <Button disabled={busy} onClick={() => void submit()}>
          {busy ? t.saving : editing ? t.saveEdit : t.save}
        </Button>
      </div>
    </div>
  );
}

/** The ARIA a ChoiceList needs to say it is wrong; the message is drawn by its Question. */
function aria(error?: string) {
  return { 'aria-invalid': error ? true : undefined };
}

/**
 * One question of options: a fieldset, so a click on an option is not handed
 * to the first one as a wrapping label would.
 */
function Question({
  n,
  label,
  hint,
  error,
  children,
}: {
  n?: string;
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  const errorId = useId();
  return (
    <fieldset
      className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0"
      aria-describedby={error ? errorId : undefined}
    >
      <legend className="mb-3 flex flex-col gap-1 p-0">
        <span className={cn('text-lg font-semibold', error && 'text-danger-text')}>
          {n !== undefined && <span className="text-ink-muted tabular-nums">{n}. </span>}
          {label}
        </span>
        {hint && <span className="text-base text-ink-muted">{hint}</span>}
      </legend>
      {children}
      {error && <FieldError id={errorId}>{error}</FieldError>}
    </fieldset>
  );
}

/** Questions only some respondents answer, set in a box so their condition reads first. */
function Branch({
  n,
  title,
  description,
  children,
}: {
  n?: string;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-6 rounded-xl border border-brand-edge/40 bg-brand-wash/40 p-4 sm:p-5">
      <div className="flex flex-col gap-1">
        <h3 className="m-0 text-base font-semibold text-brand-strong">
          {n !== undefined && <span className="tabular-nums">{n}. </span>}
          {title}
        </h3>
        {description && <p className="m-0 text-base text-ink-muted">{description}</p>}
      </div>
      {children}
    </section>
  );
}

function Banner({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
    >
      {children}
    </p>
  );
}
